/**
 * Owner group key derivation and self-encryption for cross-device sync.
 *
 * When an owner has multiple devices, each device derives the same symmetric
 * "group key" from the owner's Ed25519 identity secret key. This key encrypts
 * "self copies" of messages so any of the owner's devices can decrypt them.
 *
 * KDF: BLAKE2b-256 keyed with domain separator
 * Encryption: XChaCha20-Poly1305
 * Wire format: nonce(24) || ciphertext+tag
 */

import sodium from "libsodium-wrappers-sumo";

const DOMAIN = "agentvault-owner-sync-v1";
const NONCE_BYTES = 24; // XChaCha20-Poly1305

/**
 * Derive a deterministic 32-byte symmetric key from the owner's Ed25519
 * identity secret key. All devices sharing the same identity key will
 * produce the same group key.
 */
export function deriveOwnerGroupKey(identitySecretKey: Uint8Array): Uint8Array {
  return sodium.crypto_generichash(
    32,
    identitySecretKey,
    sodium.from_string(DOMAIN),
  );
}

/**
 * Encrypt a plaintext string for self-sync. Returns nonce || ciphertext.
 */
export function selfEncrypt(
  plaintext: string,
  groupKey: Uint8Array,
): Uint8Array {
  const nonce = sodium.randombytes_buf(NONCE_BYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    sodium.from_string(plaintext),
    null, // no additional data
    null, // secret nonce (unused, must be null)
    nonce,
    groupKey,
  );
  // nonce || ciphertext
  const result = new Uint8Array(NONCE_BYTES + ciphertext.length);
  result.set(nonce, 0);
  result.set(ciphertext, NONCE_BYTES);
  return result;
}

/**
 * Decrypt a self-sync ciphertext (nonce || ciphertext) back to plaintext.
 * Throws if the key is wrong or the data has been tampered with.
 */
export function selfDecrypt(data: Uint8Array, groupKey: Uint8Array): string {
  if (data.length <= NONCE_BYTES) {
    throw new Error("selfDecrypt: data too short");
  }
  const nonce = data.slice(0, NONCE_BYTES);
  const ciphertext = data.slice(NONCE_BYTES);
  const plainBytes = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null, // secret nonce (unused, must be null)
    ciphertext,
    null, // no additional data
    nonce,
    groupKey,
  );
  return sodium.to_string(plainBytes);
}
